import { Forum, Threads, Upload } from '@/components/ui/icons';
import { useEffect, useState, type DragEvent } from 'react';
import { safetyHoldMessage, type MessageTargetRef, type ThreadDTO } from '@clanfire/shared';
import { Avatar, GroupAvatar } from '@/components/ui/Avatar';
import { conversationTitle, recipientsOf } from '@/lib/conversations';
import { addActiveTarget, isWindowFocused } from '@/lib/navigation';
import { useChannelPermissions } from '@/lib/permissions';
import { acknowledge, isChannelUnread } from '@/lib/readState';
import { formatLater } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useBlocks, useIsBlocked } from '@/stores/blocks';
import { targetKey, useMessages, useTargetMessages } from '@/stores/messages';
import { useTyping } from '@/stores/realtime';
import { memberName, useMemberName, useServers } from '@/stores/servers';
import { isThreadUnread, useThread, useThreads } from '@/stores/threads';
import { useConversations, useReadStates } from '@/stores/social';
import { useUser, useUsers } from '@/stores/users';
import { Composer } from './Composer';
import { MessageContent } from './MessageContent';
import { ChannelWelcome, MessageList } from './MessageList';

function hasUnread(target: MessageTargetRef): boolean {
  if (target.channelId) {
    const thread = useThreads.getState().threads[target.channelId];
    const read = useReadStates.getState().channels[target.channelId];
    if (thread) return (read?.mentionCount ?? 0) > 0 || isThreadUnread(thread, read?.lastReadAt);
    const channel = useServers.getState().channels[target.channelId];
    const server = channel ? useServers.getState().servers[channel.serverId] : undefined;
    if (!channel || !server) return false;
    return (read?.mentionCount ?? 0) > 0 || isChannelUnread(channel, read?.lastReadAt, server.me.joinedAt);
  }
  const conversation = target.conversationId ? useConversations.getState().conversations[target.conversationId] : undefined;
  return (conversation?.unreadCount ?? 0) > 0;
}

function readMarker(target: MessageTargetRef): string | null {
  const thread = target.channelId ? useThreads.getState().threads[target.channelId] : undefined;
  if (thread) return useReadStates.getState().channels[thread.id]?.lastReadAt ?? thread.createdAt;
  if (target.channelId) {
    const channel = useServers.getState().channels[target.channelId];
    const server = channel ? useServers.getState().servers[channel.serverId] : undefined;
    return useReadStates.getState().channels[target.channelId]?.lastReadAt ?? server?.me.joinedAt ?? null;
  }
  return target.conversationId ? (useConversations.getState().conversations[target.conversationId]?.lastReadAt ?? null) : null;
}

/** A complete chat surface (history + typing + composer) for a channel or DM. */
export function ChatView({ target, serverId, className }: { target: MessageTargetRef; serverId?: string; className?: string }) {
  const key = targetKey(target);
  const data = useTargetMessages(key);
  const [initialReadAt] = useState(() => readMarker(target));
  const [incomingFiles, setIncomingFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const perms = useChannelPermissions(target.channelId);
  const channel = useServers((s) => (target.channelId ? s.channels[target.channelId] : undefined));
  const thread = useThread(target.channelId);
  const conversation = useConversations((s) => (target.conversationId ? s.conversations[target.conversationId] : undefined));
  const users = useUsers((s) => s.users);
  const isGroup = conversation?.type === 'GROUP';
  const recipients = conversation ? recipientsOf(conversation, users) : [];
  const recipient = isGroup ? null : (recipients[0] ?? null);
  const groupTitle = conversation ? conversationTitle(conversation, users) : '';

  useEffect(() => addActiveTarget(key), [key]);

  useEffect(() => {
    if (data.status === 'idle') void useMessages.getState().loadLatest(target);
  }, [data.status, key]);

  // Mark as read when opened and whenever the window regains focus.
  useEffect(() => {
    if (data.status !== 'loaded') return;
    if (isWindowFocused() && hasUnread(target)) acknowledge(target);
    const onFocus = () => {
      if (isWindowFocused() && hasUnread(target)) acknowledge(target);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [data.status, key]);

  const recipientBlocked = useIsBlocked(recipient?.id);
  const canSend = serverId ? perms.can('SEND_MESSAGES') : !recipientBlocked;
  const canAttach = serverId ? perms.can('ATTACH_FILES') : true;
  const placeholder = thread
    ? `Message ${thread.name}`
    : channel
      ? `Message #${channel.name}`
      : isGroup
        ? `Message ${groupTitle}`
        : `Message @${recipient?.displayName ?? ''}`;

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = [...e.dataTransfer.files];
    if (files.length) setIncomingFiles(files);
  };

  return (
    <section
      aria-label={placeholder.replace('Message ', 'Conversation in ')}
      data-cf="chat"
      className={cn('relative flex min-h-0 flex-col bg-surface', className)}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes('Files') && canSend && canAttach) setDragging(true);
      }}
      onDragOver={(e) => {
        if (dragging) e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <MessageList
        target={target}
        serverId={serverId}
        initialReadAt={initialReadAt}
        welcome={
          thread ? (
            <ThreadWelcome thread={thread} />
          ) : channel ? (
            <ChannelWelcome name={channel.name} topic={channel.topic} />
          ) : isGroup ? (
            <div className="px-4 pt-8 pb-4 sm:px-6">
              <GroupAvatar users={recipients} size={80} ring="ring-surface" />
              <h2 className="mt-3 text-3xl">{groupTitle}</h2>
              <p className="mt-2 text-fg-muted">
                This is the beginning of the <strong className="text-fg-soft">{groupTitle}</strong> group.
              </p>
            </div>
          ) : (
            <div className="px-4 pt-8 pb-4 sm:px-6">
              <Avatar user={recipient} size={80} />
              <h2 className="mt-3 text-3xl">{recipient?.displayName}</h2>
              <p className="text-lg text-fg-soft">@{recipient?.username}</p>
              <p className="mt-2 text-fg-muted">
                This is the beginning of your direct message history with <strong className="text-fg-soft">{recipient?.displayName}</strong>.
              </p>
            </div>
          )
        }
      />
      <div className="mx-auto w-full max-w-[var(--cf-chat-width)] shrink-0 px-3 pb-3 sm:px-6 sm:pb-5">
        <TypingIndicator targetKey={key} />
        <Composer
          target={target}
          serverId={serverId}
          placeholder={placeholder}
          canSend={canSend}
          canAttach={canAttach}
          disabledReason={
            perms.timedOutUntil
              ? `You're timed out until ${formatLater(perms.timedOutUntil)}. You can read, but not send messages or react.`
              : perms.rulesPending
                ? 'Accept the server rules (in the banner above) to start chatting.'
                : perms.safetyHold
                ? safetyHoldMessage(perms.safetyHold)
                : recipientBlocked
                ? 'You blocked this user. Unblock them to send messages.'
                : undefined
          }
          incomingFiles={incomingFiles}
          onIncomingConsumed={() => setIncomingFiles([])}
        />
      </div>

      {dragging && (
        <div className="pointer-events-none absolute inset-3 z-30 flex animate-fade-in flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-accent bg-surface/90 text-center">
          <Upload size={40} className="text-accent" />
          <p className="text-lg font-bold">Drop to upload</p>
          <p className="text-sm text-fg-muted">Images, video, audio, PDFs, text and zip files</p>
        </div>
      )}
    </section>
  );
}

function TypingIndicator({ targetKey: key }: { targetKey: string }) {
  const typing = useTyping((s) => s.byTarget[key]);
  const blocked = useBlocks((s) => s.ids);
  const names = Object.entries(typing ?? {})
    .filter(([userId, t]) => t.expiresAt > Date.now() && !blocked[userId])
    .map(([userId, t]) => ({ ...t, displayName: memberName(serverIdOfKey(key), userId) ?? t.displayName }))
    .map((t) => t.displayName);

  let text: React.ReactNode = null;
  if (names.length === 1) text = <><strong>{names[0]}</strong> is typing…</>;
  else if (names.length === 2) text = <><strong>{names[0]}</strong> and <strong>{names[1]}</strong> are typing…</>;
  else if (names.length === 3) text = <><strong>{names[0]}</strong>, <strong>{names[1]}</strong> and <strong>{names[2]}</strong> are typing…</>;
  else if (names.length > 3) text = 'Several people are typing…';

  return (
    <div className="flex h-6 items-center gap-2 px-2 text-xs text-fg-muted [&_strong]:text-fg-soft" aria-live="polite">
      {text && (
        <>
          <span className="flex gap-[3px]" aria-hidden>
            {[1, 0.7, 0.45].map((opacity, i) => (
              <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent" style={{ opacity, animationDelay: `${i * 120}ms` }} />
            ))}
          </span>
          <span className="truncate">{text}</span>
        </>
      )}
    </div>
  );
}

/** Server id for a channel target key (typing indicator names use server nicknames). */
function serverIdOfKey(key: string): string | null {
  if (!key.startsWith('c:')) return null;
  const id = key.slice(2);
  return useServers.getState().channels[id]?.serverId ?? useThreads.getState().threads[id]?.serverId ?? null;
}

/** The top of a thread: its name, who started it, and the message it was started from. */
function ThreadWelcome({ thread }: { thread: ThreadDTO }) {
  const owner = useUser(thread.ownerId);
  const ownerName = useMemberName(thread.serverId, thread.ownerId) ?? owner?.displayName;
  const starter = useMessages((s) => (thread.starterMessageId ? s.byTarget[targetKey({ channelId: thread.parentId })]?.messages.find((m) => m.id === thread.starterMessageId) : undefined));
  const starterAuthor = useMemberName(thread.serverId, starter?.author?.id) ?? starter?.author?.displayName;
  const isForumPost = useServers((s) => s.channels[thread.parentId]?.type === 'FORUM');
  return (
    <div className="px-4 pt-8 pb-4 sm:px-6">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-soft text-accent">
        {isForumPost ? <Forum size={32} /> : <Threads size={32} />}
      </span>
      <h2 className="mt-3 text-2xl break-words">{thread.name}</h2>
      <p className="mt-1 text-sm text-fg-muted">{ownerName ? <>Started by <strong className="text-fg-soft">{ownerName}</strong></> : 'A thread'}</p>
      {starter && starter.type === 'DEFAULT' && (
        <div className="mt-4 rounded-xl border border-line bg-sunken px-3.5 py-2.5">
          <div className="mb-1 flex items-center gap-2 text-sm">
            <Avatar user={starter.author} size={20} decorate={false} />
            <strong className="text-fg">{starterAuthor ?? 'Deleted User'}</strong>
          </div>
          <MessageContent content={starter.content} serverId={thread.serverId} className="line-clamp-6 text-[15px] leading-[22px] text-fg" />
        </div>
      )}
      {/* Forum posts never start from a message; other threads lose theirs when it's deleted. */}
      {!thread.starterMessageId && !isForumPost && <p className="mt-2 text-sm text-fg-muted italic">The message this thread started from was deleted.</p>}
    </div>
  );
}
